import { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, X, GraduationCap, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

function FormationDialog({ open, initial, cursusList, onClose, onSaved }) {
    const isEdit = !!initial?.slug;
    const empty = {
        cursus_slug: cursusList?.[0]?.slug || "",
        name: "", short_description: "", long_description: "",
        objectives: [], program_modules: [], methods: [], evaluation_methods: [],
        target_audience: "Masseurs-kinésithérapeutes diplômés d'État",
        duration_hours: 7, price_eur: 390, order: 0,
    };
    const [f, setF] = useState(initial || empty);
    useEffect(() => { setF(initial || empty); /* eslint-disable-next-line */ }, [initial, open]);
    const [saving, setSaving] = useState(false);
    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (isEdit) await api.patch(`/admin/formations/${initial.slug}`, f);
            else await api.post("/admin/formations", f);
            toast.success(isEdit ? "Formation mise à jour" : "Formation créée");
            onSaved(); onClose();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const addModule = () => setF({ ...f, program_modules: [...(f.program_modules || []), { title: "", duration: "", content: "" }] });
    const rmModule = (i) => setF({ ...f, program_modules: f.program_modules.filter((_, x) => x !== i) });

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-brand-ink/50 p-4" data-testid="formation-dialog">
            <div className="relative mx-auto mt-6 w-full max-w-3xl rounded-3xl bg-white p-8 shadow-premium-lg">
                <button onClick={onClose} className="absolute right-4 top-4 text-brand-smoke hover:text-brand-ink"><X className="h-5 w-5" /></button>
                <h3 className="font-display text-3xl">{isEdit ? "Modifier la formation" : "Nouvelle formation"}</h3>
                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider">Nom</label>
                            <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="formation-field-name" className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider">Cursus parent</label>
                            <select required value={f.cursus_slug} onChange={(e) => setF({ ...f, cursus_slug: e.target.value })} data-testid="formation-field-cursus" className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3">
                                {(cursusList || []).map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider">Durée (h)</label>
                            <input type="number" value={f.duration_hours} onChange={(e) => setF({ ...f, duration_hours: parseInt(e.target.value || 7) })} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider">Prix (€)</label>
                            <input type="number" value={f.price_eur} onChange={(e) => setF({ ...f, price_eur: parseInt(e.target.value || 390) })} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Description courte</label>
                        <input value={f.short_description} onChange={(e) => setF({ ...f, short_description: e.target.value })} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Description longue</label>
                        <textarea rows={3} value={f.long_description} onChange={(e) => setF({ ...f, long_description: e.target.value })} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3" />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Objectifs (1 par ligne)</label>
                        <textarea rows={3} value={(f.objectives || []).join("\n")} onChange={(e) => setF({ ...f, objectives: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3" />
                    </div>

                    <div>
                        <div className="flex items-center justify-between">
                            <label className="text-xs font-semibold uppercase tracking-wider">Modules du programme</label>
                            <button type="button" onClick={addModule} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-orange hover:underline"><Plus className="h-3.5 w-3.5" /> Ajouter</button>
                        </div>
                        <div className="mt-3 space-y-3">
                            {(f.program_modules || []).length === 0 && <div className="rounded-xl border border-dashed border-brand-line p-4 text-center text-xs text-brand-smoke">Aucun module. Ajoutez-en au moins un.</div>}
                            {(f.program_modules || []).map((m, i) => (
                                <div key={i} className="relative rounded-xl border border-brand-line bg-[#FAFAFA] p-4">
                                    <button type="button" onClick={() => rmModule(i)} className="absolute right-2 top-2 text-brand-smoke hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                        <input placeholder="Titre" value={m.title} onChange={(e) => { const mm = [...f.program_modules]; mm[i] = { ...m, title: e.target.value }; setF({ ...f, program_modules: mm }); }} className="rounded-lg border border-brand-line bg-white px-3 py-2 text-sm" />
                                        <input placeholder="Durée (ex: 2h)" value={m.duration} onChange={(e) => { const mm = [...f.program_modules]; mm[i] = { ...m, duration: e.target.value }; setF({ ...f, program_modules: mm }); }} className="rounded-lg border border-brand-line bg-white px-3 py-2 text-sm" />
                                    </div>
                                    <textarea rows={2} placeholder="Contenu" value={m.content} onChange={(e) => { const mm = [...f.program_modules]; mm[i] = { ...m, content: e.target.value }; setF({ ...f, program_modules: mm }); }} className="mt-2 w-full rounded-lg border border-brand-line bg-white px-3 py-2 text-sm" />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider">Méthodes (1 / ligne)</label>
                            <textarea rows={3} value={(f.methods || []).join("\n")} onChange={(e) => setF({ ...f, methods: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 text-sm" />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold uppercase tracking-wider">Évaluation (1 / ligne)</label>
                            <textarea rows={3} value={(f.evaluation_methods || []).join("\n")} onChange={(e) => setF({ ...f, evaluation_methods: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 text-sm" />
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm hover:bg-brand-line/50">Annuler</button>
                        <button type="submit" disabled={saving} data-testid="formation-dialog-submit" className="rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60">
                            {saving ? "Enregistrement…" : (isEdit ? "Enregistrer" : "Créer")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function ImportDialog({ open, cursusList, onClose, onSaved }) {
    const [formateurs, setFormateurs] = useState([]);
    const [f, setF] = useState({ formateur_id: "", cursus_slug: cursusList?.[0]?.slug || "" });
    useEffect(() => {
        if (!open) return;
        api.get("/admin/users", { params: { role: "formateur" } })
            .then((r) => setFormateurs(r.data.filter((u) => u.onboarding_completed)))
            .catch(() => {});
    }, [open]);
    useEffect(() => { setF((x) => ({ ...x, cursus_slug: cursusList?.[0]?.slug || "" })); }, [cursusList]);
    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/admin/formations/import-from-programme", f);
            toast.success("Formation importée depuis le programme formateur");
            onSaved(); onClose();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/50 p-4" data-testid="import-dialog">
            <div className="relative w-full max-w-lg rounded-3xl bg-white p-8 shadow-premium-lg">
                <button onClick={onClose} className="absolute right-4 top-4 text-brand-smoke hover:text-brand-ink"><X className="h-5 w-5" /></button>
                <h3 className="font-display text-3xl">Importer depuis un programme formateur</h3>
                <p className="mt-2 text-sm text-brand-smoke">Convertit une trame 13 sections soumise en formation officielle rattachée à un cursus.</p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Formateur</label>
                        <select required value={f.formateur_id} onChange={(e) => setF({ ...f, formateur_id: e.target.value })} className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3">
                            <option value="">— Sélectionner —</option>
                            {formateurs.map((u) => <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Cursus de rattachement</label>
                        <select required value={f.cursus_slug} onChange={(e) => setF({ ...f, cursus_slug: e.target.value })} className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3">
                            {cursusList.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm hover:bg-brand-line/50">Annuler</button>
                        <button type="submit" data-testid="import-dialog-submit" className="rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c]">Importer</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function AdminFormations() {
    const [items, setItems] = useState([]);
    const [cursusList, setCursusList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterCursus, setFilterCursus] = useState("");
    const [dialog, setDialog] = useState({ open: false, initial: null });
    const [importOpen, setImportOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [f, c] = await Promise.all([api.get("/formations"), api.get("/cursus")]);
            setItems(f.data);
            setCursusList(c.data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => items.filter((f) => !filterCursus || f.cursus_slug === filterCursus), [items, filterCursus]);

    const remove = async (f) => {
        if (!window.confirm(`Supprimer la formation "${f.name}" ?`)) return;
        try { await api.delete(`/admin/formations/${f.slug}`); toast.success("Formation supprimée"); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="mx-auto max-w-6xl px-6 py-10" data-testid="page-admin-formations">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="font-display text-5xl font-normal tracking-tightest">Formations</h1>
                    <p className="mt-2 text-brand-smoke">Chaque formation est rattachée à un cursus et génère ses sessions.</p>
                </div>
                <div className="flex gap-3">
                    <button onClick={() => setImportOpen(true)} className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-5 py-3 text-sm font-semibold text-brand-ink hover:border-brand-orange">
                        <Upload className="h-4 w-4" /> Importer d'un programme
                    </button>
                    <button onClick={() => setDialog({ open: true, initial: null })} data-testid="admin-formations-new" className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-3 text-sm font-semibold text-white hover:bg-[#d9772c]">
                        <Plus className="h-4 w-4" /> Nouvelle formation
                    </button>
                </div>
            </div>

            <div className="mt-6 flex gap-3">
                <select value={filterCursus} onChange={(e) => setFilterCursus(e.target.value)} className="rounded-full border border-brand-line bg-white px-4 py-2.5 text-sm">
                    <option value="">Tous cursus</option>
                    {cursusList.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
            </div>

            <div className="mt-6 overflow-hidden rounded-2xl border border-brand-line bg-white shadow-premium">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-brand-line bg-[#FAFAFA] text-left text-xs uppercase tracking-wider text-brand-smoke">
                            <th className="px-6 py-4">Formation</th>
                            <th className="px-6 py-4">Cursus</th>
                            <th className="px-6 py-4">Durée</th>
                            <th className="px-6 py-4">Prix</th>
                            <th className="px-6 py-4 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-brand-line">
                        {loading ? (
                            <tr><td colSpan={5} className="px-6 py-10 text-center text-brand-smoke">Chargement…</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan={5} className="px-6 py-10 text-center text-brand-smoke">Aucune formation.</td></tr>
                        ) : (
                            filtered.map((f) => (
                                <tr key={f.slug} className="hover:bg-[#FAFAFA]" data-testid={`formation-row-${f.slug}`}>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange"><GraduationCap className="h-4 w-4" /></div>
                                            <div>
                                                <strong className="text-brand-ink">{f.name}</strong>
                                                <div className="text-xs text-brand-smoke">{f.short_description}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-brand-smoke">{f.cursus_slug}</td>
                                    <td className="px-6 py-4">{f.duration_hours}h</td>
                                    <td className="px-6 py-4 font-display text-lg">{f.price_eur} €</td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="inline-flex gap-2">
                                            <button onClick={() => setDialog({ open: true, initial: f })} data-testid={`formation-edit-${f.slug}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange"><Pencil className="h-3.5 w-3.5" /></button>
                                            <button onClick={() => remove(f)} data-testid={`formation-delete-${f.slug}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <FormationDialog open={dialog.open} initial={dialog.initial} cursusList={cursusList} onClose={() => setDialog({ open: false, initial: null })} onSaved={load} />
            <ImportDialog open={importOpen} cursusList={cursusList} onClose={() => setImportOpen(false)} onSaved={load} />
        </div>
    );
}
